import type { CreativeEntry } from 'types/shared';
import type { UploadDate } from '../../creatives';

export type HypothesisSection = 'this_week' | 'upcoming';

export type LaunchStatus = 'launched' | 'not_launched';

export type UploadedFilter = UploadDate;

export interface HypothesisCreativeEntry extends CreativeEntry {
  hypothesis: string | null;
  section: HypothesisSection | null;
  launch_status: LaunchStatus;
  flow: string | null;
  customer: string | null;
  tpc_finish_date: string | null;
  tpc_model: string | null;
  concept: string | null;
  variable: string | null;
  idea_type: string | null;
  headline: string | null;
  technology: string | null;
  preview_link: string | null;
  uploaded: string;
}

export interface HypothesisGroup {
  hypothesis: string;
  section: HypothesisSection | null;
  creatives: HypothesisCreativeEntry[];
}

export type CreativeDnDZone =
  | { kind: 'pack'; packIndex: number }
  | { kind: 'unpacked' };

export interface CreativeDnDMovePayload {
  creativeId: string;
  from: CreativeDnDZone & { index: number };
  to: CreativeDnDZone & { index: number };
}

export type CreativeColumnKey =
  | 'dragHandle'
  | 'preview'
  | 'name'
  | 'flow'
  | 'customer'
  | 'tpc'
  | 'creative_type'
  | 'concept'
  | 'variable'
  | 'idea_type'
  | 'headline'
  | 'technology'
  | 'uploaded'
  | 'status';

export const DEFAULT_CREATIVE_COLUMNS: CreativeColumnKey[] = [
  'dragHandle',
  'preview',
  'name',
  'flow',
  'customer',
  'tpc',
  'creative_type',
  'concept',
  'variable',
  'idea_type',
  'headline',
  'technology',
  'uploaded',
  'status',
];

export const CREATIVE_COLUMN_LABELS: Record<CreativeColumnKey, string> = {
  dragHandle: '',
  preview: 'Preview',
  name: 'Creative name',
  flow: 'Flow',
  customer: 'Customer',
  tpc: 'TPC',
  creative_type: 'Type',
  concept: 'Concept',
  variable: 'Variable',
  idea_type: 'Idea type',
  headline: 'Headline',
  technology: 'Technology',
  uploaded: 'Uploaded',
  status: 'Status',
};

export const UPLOADED_FILTER_OPTIONS: { value: UploadedFilter; label: string }[] = [
  { value: 'last_week', label: 'This week' },
  { value: 'last_2_weeks', label: 'Last 2 weeks' },
  { value: '1_month', label: '1 month' },
  { value: 'all', label: 'All' },
];

export const LAUNCH_STATUS_OPTIONS: { value: LaunchStatus; label: string }[] = [
  { value: 'launched', label: 'Launched' },
  { value: 'not_launched', label: 'Not launched' },
];

export interface TestCreativesViewState {
  search: string;
  hypothesis: string[];
  customer: string | null;
  flow: string[];
  technology: string[];
  tpcFinishDate: string | null;
  status: LaunchStatus | null;
  uploaded: UploadedFilter;
  groupByHypothesis: boolean;
  unpackedOrderByHypothesis: Record<string, string[]>;
}

export const DEFAULT_TEST_CREATIVES_VIEW_STATE: TestCreativesViewState = {
  search: '',
  hypothesis: [],
  customer: null,
  flow: [],
  technology: [],
  tpcFinishDate: null,
  status: null,
  uploaded: 'last_week',
  groupByHypothesis: true,
  unpackedOrderByHypothesis: {},
};

export interface SavedCreativeFilters {
  search: string;
  uploaded: UploadedFilter;
  hypothesis: string[];
  flow: string[];
  customer: string | null;
  technology: string[];
  tpcFinishDate: string | null;
  status: LaunchStatus | null;
}

export interface SavedCreativeFiltersAppEntry {
  enabled: boolean;
  filters: SavedCreativeFilters;
  /** ISO timestamp of the last save/update. */
  savedAt: string;
}

export interface SavedCreativeFiltersStorage {
  version: 1;
  byApp: Record<string, SavedCreativeFiltersAppEntry>;
}

export const DEFAULT_SAVED_CREATIVE_FILTERS: SavedCreativeFilters = {
  search: '',
  uploaded: 'last_week',
  hypothesis: [],
  flow: [],
  customer: null,
  technology: [],
  tpcFinishDate: null,
  status: null,
};
